import React, { useState, useEffect } from 'react';
import './AccessibilityWidget.css';

const STORAGE_KEY = 'trabelci-a11y-settings';

const DEFAULT_SETTINGS = {
  fontLevel: 0,
  highContrast: false,
  highlightLinks: false,
  readableFont: false,
  reduceMotion: false,
};

const FONT_CLASSES = ['a11y-font-lg', 'a11y-font-xl'];

const loadSettings = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return { ...DEFAULT_SETTINGS };
    return { ...DEFAULT_SETTINGS, ...JSON.parse(saved) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
};

const applySettings = (settings) => {
  const root = document.documentElement;

  FONT_CLASSES.forEach((cls) => root.classList.remove(cls));
  if (settings.fontLevel > 0) {
    root.classList.add(FONT_CLASSES[settings.fontLevel - 1]);
  }

  root.classList.toggle('a11y-high-contrast', settings.highContrast);
  root.classList.toggle('a11y-highlight-links', settings.highlightLinks);
  root.classList.toggle('a11y-readable-font', settings.readableFont);
  root.classList.toggle('a11y-reduce-motion', settings.reduceMotion);
};

applySettings(loadSettings());

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(loadSettings);

  useEffect(() => {
    applySettings(settings);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const increaseFont = () => {
    setSettings((prev) => ({
      ...prev,
      fontLevel: Math.min(2, prev.fontLevel + 1),
    }));
  };

  const decreaseFont = () => {
    setSettings((prev) => ({
      ...prev,
      fontLevel: Math.max(0, prev.fontLevel - 1),
    }));
  };

  const resetSettings = () => {
    setSettings({ ...DEFAULT_SETTINGS });
  };

  const toggleSetting = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="a11y-widget" dir="rtl">
      {open && (
        <div
          className="a11y-panel"
          role="dialog"
          aria-label="תפריט נגישות"
        >
          <div className="a11y-panel-header">
            <h2 className="a11y-panel-title">נגישות</h2>
            <button
              type="button"
              className="a11y-panel-close"
              onClick={() => setOpen(false)}
              aria-label="סגירת תפריט נגישות"
            >
              ×
            </button>
          </div>

          <div className="a11y-panel-body">
            <div className="a11y-option">
              <span className="a11y-option-label">גודל טקסט</span>
              <div className="a11y-font-controls">
                <button type="button" onClick={decreaseFont} aria-label="הקטנת טקסט">A-</button>
                <span className="a11y-font-level">{settings.fontLevel + 1}/3</span>
                <button type="button" onClick={increaseFont} aria-label="הגדלת טקסט">A+</button>
              </div>
            </div>

            <button
              type="button"
              className={`a11y-toggle ${settings.highContrast ? 'active' : ''}`}
              onClick={() => toggleSetting('highContrast')}
              aria-pressed={settings.highContrast}
            >
              ניגודיות גבוהה
            </button>

            <button
              type="button"
              className={`a11y-toggle ${settings.highlightLinks ? 'active' : ''}`}
              onClick={() => toggleSetting('highlightLinks')}
              aria-pressed={settings.highlightLinks}
            >
              הדגשת קישורים
            </button>

            <button
              type="button"
              className={`a11y-toggle ${settings.readableFont ? 'active' : ''}`}
              onClick={() => toggleSetting('readableFont')}
              aria-pressed={settings.readableFont}
            >
              גופן קריא
            </button>

            <button
              type="button"
              className={`a11y-toggle ${settings.reduceMotion ? 'active' : ''}`}
              onClick={() => toggleSetting('reduceMotion')}
              aria-pressed={settings.reduceMotion}
            >
              עצירת אנימציות
            </button>

            <button type="button" className="a11y-reset" onClick={resetSettings}>
              איפוס הגדרות
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className={`a11y-fab ${open ? 'a11y-fab--open' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-label={open ? 'סגירת תפריט נגישות' : 'פתיחת תפריט נגישות'}
        aria-expanded={open}
        title="נגישות"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" className="a11y-fab-icon">
          <path
            fill="currentColor"
            d="M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm8 7h-2.59c-.89 0-1.68.47-2.11 1.24l-1.41 2.44c-.37.64-.37 1.42 0 2.06l.72 1.25c.28.48.28 1.07 0 1.55l-1.25 2.16c-.43.74-1.22 1.2-2.11 1.2H14v5c0 .55-.45 1-1 1h-2c-.55 0-1-.45-1-1v-5H6.59c-.89 0-1.68-.46-2.11-1.2L3.23 17.5a1.5 1.5 0 0 1 0-1.55l.72-1.25c.28-.48.28-1.06 0-1.55l-1.41-2.44A2.5 2.5 0 0 0 .41 9H2c1.1 0 2-.9 2-2V5c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v2c0 1.1-.9 2-2 2z"
          />
        </svg>
      </button>
    </div>
  );
}
